import React, { useEffect, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Alert, Card, Col, Row, Table, ButtonGroup, Button, Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { getToken } from '../services/authorize'
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from '../contexts/LoadingContext';
import { useParams, useLocation } from "react-router-dom";

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');
const StudentAvatar = ({ photo, name, fill = false }) => {
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const initial = name ? name.trim().charAt(0) : '?';
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null;

  if (fill) {
    return (
      <>
        <div
          style={{ width: '100%', height: '100%', cursor: src ? 'pointer' : 'default' }}
          onClick={() => src && setShowModal(true)}
        >
          {src
            ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <div style={{ width: '100%', height: '100%', backgroundColor: '#adb5bd', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 600 }}>{initial}</div>
          }
        </div>
        {src && (
          <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
            <Modal.Body className="p-3 text-center">
              <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
              {name && <div className="mt-2 fw-semibold small">{name}</div>}
            </Modal.Body>
          </Modal>
        )}
      </>
    );
  }

  const circleStyle = { width: 28, height: 28, borderRadius: '50%', flexShrink: 0 };
  return (
    <>
      <span
        style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}
        onMouseEnter={() => src && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => src && setShowModal(true)}
      >
        {src
          ? <img src={src} alt="" style={{ ...circleStyle, objectFit: 'cover', cursor: 'pointer' }} />
          : <span style={{ ...circleStyle, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }}>{initial}</span>
        }
        {src && hovered && (
          <div style={{ position: 'absolute', left: 34, top: '50%', transform: 'translateY(-50%)', zIndex: 1050, background: '#fff', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', padding: 4 }}>
            <img src={src} alt={name} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
          </div>
        )}
      </span>
      {src && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
          <Modal.Body className="p-3 text-center">
            <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
            {name && <div className="mt-2 fw-semibold small">{name}</div>}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

const isInactiveStudent = (student) => String(student.st_status) === '0';
const isTransferredStudent = (student) => !!student.transfer_room_id;

// ป้ายกำกับเมื่อมีการแจ้งลานักเรียนไว้ล่วงหน้า (ครูประจำชั้น หรือ ฝ่ายปกครอง)
const LeaveBadge = ({ leave }) => {
  if (!leave) return null;
  const label = leave.leave_type === 'sick' ? 'ป่วย' : 'กิจ';
  const by = leave.source === 'discipline' ? 'ฝ่ายปกครองแจ้งลา' : 'ครูประจำชั้นแจ้งลา';
  return (
    <span className="badge bg-info ms-2" title={leave.leave_note || ''}>
      <i className="ri-calendar-close-line me-1"></i>{by} ({label})
    </span>
  );
};

// ตรวจจับหน้าจอมือถือ (max-width: 768px)
const useIsMobile = (query = '(max-width: 768px)') => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return isMobile;
};

// ปุ่มเลือกสถานะการเข้าเรียน (ใช้ร่วมกันทั้งตารางและการ์ด)
const AttendanceButtons = ({ student, index, onChange, size = 'sm' }) => (
  <ButtonGroup>
    <Button variant={student.attendanceStatus === 'present' ? 'success' : 'outline-success'} size={size} onClick={() => onChange(index, 'present')}>มา</Button>
    <Button variant={student.attendanceStatus === 'late' ? 'warning' : 'outline-warning'} size={size} onClick={() => onChange(index, 'late')}>สาย</Button>
    <Button variant={student.attendanceStatus === 'leave' ? 'info' : 'outline-info'} size={size} onClick={() => onChange(index, 'leave')}>ลา</Button>
    <Button variant={student.attendanceStatus === 'absent' ? 'danger' : 'outline-danger'} size={size} onClick={() => onChange(index, 'absent')}>ขาด</Button>
  </ButtonGroup>
);

export default function CheckAttendance() {
  const { r_id, teach_id } = useParams();
  const location = useLocation()
  const periods = location.state?.periods || [];
  const { setLoading } = useLoading();
  const currentSkin = (localStorage.getItem('skin-mode')) ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const isMobile = useIsMobile();
  // โหมดแสดงผล: บนมือถือเริ่มต้นเป็น 'card', บนจอใหญ่เป็น 'table'
  // ผู้ใช้สลับเองได้ (เก็บการเลือกของผู้ใช้ไว้ ไม่ให้ถูก override เมื่อ resize)
  const [viewMode, setViewMode] = useState(null);
  const effectiveView = viewMode ?? (isMobile ? 'card' : 'table');

  const navigate = useNavigate();

  const switchSkin = (skin) => {
    if (skin === 'dark') {
      const btnWhite = document.getElementsByClassName('btn-white');
      for (const btn of btnWhite) {
        btn.classList.add('btn-outline-primary');
        btn.classList.remove('btn-white');
      }
    } else {
      const btnOutlinePrimary = document.getElementsByClassName('btn-outline-primary');
      for (const btn of btnOutlinePrimary) {
        btn.classList.remove('btn-outline-primary');
        btn.classList.add('btn-white');
      }
    }
    const tables = document.getElementsByTagName('table');
    for (const table of tables) {
      const theads = table.querySelectorAll('thead');
      if (skin === 'dark') {
        for (const thead of theads) {
          thead.classList.remove('table-secondary');
          thead.classList.add('table-primary');
        }
        table.classList.add('table-dark');
      } else {
        table.classList.remove('table-dark');
        for (const thead of theads) {
          thead.classList.remove('table-primary');
          thead.classList.add('table-secondary');
        }
      }
    }
  }

  switchSkin(skin);

  useEffect(() => {
    switchSkin(skin);
  }, [skin]);

  const [data, setData] = useState([]);
  // เลือกวันที่ (default วันนี้)
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    return today;
  });

  // เลือกคาบเรียน
  const [room, setRoom] = useState(null)
  const [subject, setSubject] = useState(null)
  const [topic, setTopic] = useState(null);
  const [alreadyChecked, setAlreadyChecked] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // กันครูกดปุ่มบันทึกรัว ๆ

  // กำหนด key สำหรับ localStorage
  const attendanceKey = `attendance_${teach_id}_${selectedDate}_${periods}`;

  // Function to get attendance data from localStorage
  const getSavedAttendance = React.useCallback(() => {
    const saved = localStorage.getItem(attendanceKey);
    return saved ? JSON.parse(saved) : null;
  }, [attendanceKey]);

  // Modify the useEffect for loading students data
  useEffect(() => {
    setLoading(true);
    const authHeader = { headers: { authorization: `Bearer ${getToken()}` } };
    Promise.all([
      axiosInstance.get(`${process.env.REACT_APP_API}/listStudentsInClass/${r_id}`, authHeader),
      // รายการลาที่ครูประจำชั้นแจ้งไว้สำหรับวันที่เลือก (พังก็ไม่ block หน้า)
      axiosInstance.get(`${process.env.REACT_APP_API}/getLeavesByRoomDate/${r_id}/${selectedDate}`, authHeader)
        .then(res => res.data).catch(() => []),
    ])
    .then(([response, leaves]) => {
      const leaveMap = new Map((leaves || []).map(l => [l.study_id, l]));
      const savedData = getSavedAttendance();
      if (savedData) {
        // มี draft อยู่แล้ว: คงสถานะที่ครูติ๊กไว้ แต่ refresh badge การลา
        setData(savedData.map(student => ({
          ...student,
          advisorLeave: leaveMap.get(student.study_id) || null,
        })));
      } else {
        // ไม่มี draft: auto-select 'ลา' ให้นักเรียนที่ครูประจำชั้นแจ้งลาไว้
        setData(response.data.map(student => ({
          ...student,
          advisorLeave: leaveMap.get(student.study_id) || null,
          attendanceStatus: (isInactiveStudent(student) || isTransferredStudent(student))
            ? 'absent'
            : (leaveMap.has(student.study_id) ? 'leave' : 'present'),
        })));
      }
    })
    .catch(err => {
      Swal.fire({
        title: err.response?.data?.title || err.response?.statusText || 'เกิดข้อผิดพลาด',
        text: err.response?.data?.message || err.message,
        icon: "error"
      })
    })
    .finally(() => {
      setLoading(false);
    });
  }, [setLoading, r_id, selectedDate, attendanceKey, getSavedAttendance]); // Add attendanceKey to dependencies

  // ทุกครั้งที่ data เปลี่ยน ให้บันทึกลง localStorage เฉพาะเมื่อเลือกคาบแล้ว
  useEffect(() => {
    if (data && data.length > 0) {
      localStorage.setItem(attendanceKey, JSON.stringify(data));
    }
  }, [data, attendanceKey]);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getSubjectAndRoomByTeachId/${teach_id}`, {
      headers: {
        authorization: `Bearer ${getToken()}`
      }
    })
    .then(response => {
      const { tb_class_c_id, r_no, sub_subname } = response.data[0]
      if (response.data.length > 0) {
        setRoom(tb_class_c_id + "/" + r_no);
        setSubject(sub_subname);
      } else {
        Swal.fire({
          title: "ไม่สามารถโหลดข้อมูลได้",
          text: "ไม่พบข้อมูลห้องเรียนหรือวิชาเรียน",
          icon: "warning"
        })
      }
    })
    .catch(err => {
      console.error(err);
      Swal.fire({
        title: err.response.statusText,
        text: err.message,
        icon: "error"
      })
    })
    .finally(() => {
      setLoading(false);
    })
  }, [setLoading, teach_id]);        

  useEffect(() => {
    if (!teach_id || !selectedDate || !periods || periods.length === 0) return;
    axiosInstance.get(`${process.env.REACT_APP_API}/getAttendanceForEdit/${teach_id}/${selectedDate}/${periods[0]}`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
    .then(res => {
      setAlreadyChecked(res.data && res.data.length > 0);
    })
    .catch(() => setAlreadyChecked(false));
  }, [teach_id, selectedDate, periods]);

  const handleAttendanceChange = (index, status) => {
    const updated = [...data];
    updated[index].attendanceStatus = status;
    setData(updated);
    // ไม่ต้อง set localStorage ที่นี่ เพราะ useEffect ข้างบนจะจัดการให้
  }

  // เพิ่มฟังก์ชันสำหรับบันทึกการเข้าเรียน
  const handleSaveAttendance = () => {
    if (isSaving) return; // กันการยิงซ้ำระหว่างที่ request ก่อนหน้ายังไม่ตอบ
    if (!topic) {
      const topicInput = document.getElementById('topicInput');
      topicInput.classList.add('is-invalid');
      topicInput?.focus();
      return;
    }
    setIsSaving(true);
    setLoading(true);
    axiosInstance.post(`${process.env.REACT_APP_API}/saveAttendance`, {
      date: selectedDate,
      periods,
      teach_id: teach_id,
      topic: topic,
      students: data
        .filter(student => !isInactiveStudent(student) && !isTransferredStudent(student))
        .map(student => ({
          study_id: student.study_id,
          attendanceStatus: student.attendanceStatus
        }))
    }, {
      headers: {
        authorization: `Bearer ${getToken()}`
      }
    })
    .then(() => {
      localStorage.removeItem(attendanceKey); // ลบข้อมูลที่เก็บไว้
      Swal.fire({
        title: "บันทึกการเช็คชื่อสำเร็จ",
        icon: "success"
      }).then(() => {
        navigate(`/teaching/edit-attendance/${teach_id}/${selectedDate}/${periods[0]}`);
      })
    })
    .catch(err => {
      // console.error(err);
      localStorage.removeItem(attendanceKey); // ลบข้อมูลที่เก็บไว้
      Swal.fire({
        title: err.response.data.title || err.response.statusText,
        text: err.response.data.message,
        icon: "error"
      })
    })
    .finally(() => {
      setLoading(false);
      setIsSaving(false);
    });
  }

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ข้อมูลการสอน</Link></li>
              <li className="breadcrumb-item"><Link to="#">เช็คชื่อนักเรียน</Link></li>
              <li className="breadcrumb-item active" aria-current="page">ชั้น ม.{room}</li>
            </ol>
            <h4 className="main-title mb-0">เช็คชื่อนักเรียน ม.{room} วิชา {subject}</h4>
          </div>
        </div>

        {alreadyChecked && (
          <Alert variant="warning" className="d-flex align-items-center gap-2 mb-3">
            <i className="ri-error-warning-line fs-5"></i>
            <span>วิชานี้เช็คชื่อไปแล้วในวันนี้ — หากต้องการแก้ไข <Link to={`/teaching/edit-attendance/${teach_id}/${selectedDate}/${periods[0]}`} className="text-decoration-underline">กดที่นี่</Link></span>
          </Alert>
        )}

        <Row className="g-12 mb-4">
          <div className="d-flex flex-wrap align-items-center gap-2 mt-3 mt-md-0">
            {/* เลือกวันที่ */}
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0 ps-3" style={{ minWidth: '50px' }}>วันที่:</label>
              <input
                type="date"
                className="form-control"
                style={{ maxWidth: '180px' }}
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
            {/* หัวข้อที่สอน */}
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0 ps-3" style={{ minWidth: '50px' }}>หัวข้อที่สอน:</label>
              <input
                type="text"
                id="topicInput"
                className="form-control"
                style={{ minWidth: '300px', maxWidth: '800px', width: '420px' }}
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="ระบุเรื่องที่สอน หรือกิจกรรมที่ให้นักเรียนทำ"
              />
            </div>
          </div>
          <Col xl="12">
            <Card className="card-one">
              <Card.Body className="p-3">
                {/* ปุ่มสลับมุมมอง ตาราง / การ์ด */}
                <div className="d-flex justify-content-end mb-3">
                  <ButtonGroup size="sm">
                    <Button
                      variant={effectiveView === 'card' ? 'primary' : 'outline-primary'}
                      onClick={() => setViewMode('card')}
                      title="มุมมองการ์ด"
                    >
                      <i className="ri-layout-grid-line"></i> การ์ด
                    </Button>
                    <Button
                      variant={effectiveView === 'table' ? 'primary' : 'outline-primary'}
                      onClick={() => setViewMode('table')}
                      title="มุมมองตาราง"
                    >
                      <i className="ri-table-line"></i> ตาราง
                    </Button>
                  </ButtonGroup>
                </div>

                {effectiveView === 'table' ? (
                  <Table responsive hover className="mb-0">
                    <thead>
                      <tr>
                        <th scope="col" className="text-center" style={{ width:"50px" }}>เลขที่</th>
                        <th scope="col" className="text-center" style={{ width: "100px" }}>เลขประจำตัว</th>
                        <th scope="col">ชื่อ-สกุล</th>
                        <th scope="col" className="text-center" style={{ width: "150px" }}>สถานะการเข้าเรียน</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data && data.length > 0 ? (
                        data.map((student, index) => (
                          <tr key={student.study_id} className={isInactiveStudent(student) ? 'table-danger' : isTransferredStudent(student) ? 'table-warning' : ''}>
                            <th scope="row" className="text-center">{index + 1}</th>
                            <td className="text-center">{student.st_idnumber}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <StudentAvatar photo={student.st_photo} name={student.st_name} />
                                <span>
                                  {student.st_name}
                                  {isTransferredStudent(student) && (
                                    <small className="text-muted ms-2">(ย้ายไป ม.{student.transfer_class_id}/{student.transfer_room_no})</small>
                                  )}
                                  <LeaveBadge leave={student.advisorLeave} />
                                </span>
                              </div>
                            </td>
                            <td className="text-center">
                              {!isInactiveStudent(student) && !isTransferredStudent(student) && (
                                <AttendanceButtons student={student} index={index} onChange={handleAttendanceChange} />
                              )}
                              {isInactiveStudent(student) && (
                                <span className="text-danger">จำหน่าย</span>
                              )}
                              {isTransferredStudent(student) && (
                                <span className="text-warning">ย้ายห้อง</span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan={4} className="text-center">ไม่พบข้อมูล</td></tr>
                      )}
                    </tbody>
                  </Table>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {data && data.length > 0 ? (
                      data.map((student, index) => (
                        <Card
                          key={student.study_id}
                          className={`border ${isInactiveStudent(student) ? 'border-danger bg-danger bg-opacity-10' : isTransferredStudent(student) ? 'border-warning bg-warning bg-opacity-10' : ''}`}
                        >
                          <Card.Body className="p-0" style={{ minHeight: 76 }}>
                            <div className="d-flex" style={{ minHeight: 76 }}>
                              {/* รูปใหญ่ชิดซ้าย */}
                              <div style={{ width: 64, flexShrink: 0, overflow: 'hidden', borderRadius: 'calc(0.375rem - 1px) 0 0 calc(0.375rem - 1px)' }}>
                                <StudentAvatar photo={student.st_photo} name={student.st_name} fill />
                              </div>
                              {/* ข้อมูล + ปุ่ม */}
                              <div className="flex-grow-1 px-2 py-2 d-flex flex-column justify-content-between">
                                <div className="d-flex align-items-start gap-2">
                                  <span className="badge bg-secondary flex-shrink-0 mt-1">{index + 1}</span>
                                  <div className="flex-grow-1 text-end">
                                    <div className="fw-semibold" style={{ fontSize: '0.875rem' }}>{student.st_name}</div>
                                    <small className="text-muted">เลขประจำตัว {student.st_idnumber}</small>
                                    {isTransferredStudent(student) && (
                                      <div><small className="text-muted">(ย้ายไป ม.{student.transfer_class_id}/{student.transfer_room_no})</small></div>
                                    )}
                                    {student.advisorLeave && <div className="mt-1"><LeaveBadge leave={student.advisorLeave} /></div>}
                                  </div>
                                </div>
                                <div className="d-flex justify-content-end">
                                  {!isInactiveStudent(student) && !isTransferredStudent(student) && (
                                    <AttendanceButtons student={student} index={index} onChange={handleAttendanceChange} />
                                  )}
                                  {isInactiveStudent(student) && (
                                    <span className="text-danger">จำหน่าย</span>
                                  )}
                                  {isTransferredStudent(student) && (
                                    <span className="text-warning">ย้ายห้อง</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      ))
                    ) : (
                      <div className="text-center text-muted py-3">ไม่พบข้อมูล</div>
                    )}
                  </div>
                )}

                <div className="d-flex justify-content-end mt-3">
                  <Button variant="success" size="sm" onClick={handleSaveAttendance} disabled={isSaving}><i className="ri-save-3-fill"> {isSaving ? 'กำลังบันทึก...' : 'บันทึกการเข้าเรียน'}</i></Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </React.Fragment>
  );
}
